import { ComponentProps, useEffect, useState, useMemo, Fragment } from "react";
import {
  CurrencyType,
  currencySymbolMapper,
  getCurrencyFromCountryCode,
} from "../utils/currency";
import { useTranslation } from "react-i18next";
import useAuth from "../hooks/useAuth";
import { useNavigate, useSearchParams } from "react-router-dom";
import { CustomButton } from "./atoms/CustomButton";
import { useGetEntityUser } from "../hooks/users";
import { useFreeSubscription, useGetOwnerPlans } from "../hooks/billing";
import { useOwnderLoginSocial } from "../hooks/entity";
import SecureStorage from "../utils/secureStorage";
import { toast } from "react-toastify";
import {
  HiOutlineChatAlt2,
  HiOutlineGift,
  HiOutlineIdentification,
  HiOutlineCode,
  HiSupport,
  HiOutlineUsers,
  HiOutlineFilter,
  HiOutlineCog,
  HiOutlineCheckCircle,
  HiCurrencyDollar,
  HiOutlineSearch,
  HiPencilAlt,
  HiOutlineClipboardList,
  HiOutlineSparkles,
  HiOutlineViewBoards,
} from "react-icons/hi";
import {
  getDifferentialPlanFeatures,
  PlanTier,
  USER_LIMITS,
  RATE_LIMITS,
  LEAD_LIMITS,
  TODO_BOARD_LIMITS,
  FEATURE_CATEGORIES,
  FeatureCategory,
  usePermissions,
} from "../utils/permissions";
import { useGetTrialStatus } from "../hooks/promoCode";
import { HiTicket } from "react-icons/hi2";
import { usePlanChanges } from "../hooks/billing/planChange";
import PlanChangeModal from "./planChange/PlanChangeModal";
import {
  classifyPlanChange,
  findSelfPaidPlan,
  PaidTier,
  PlanChangeKind,
  planInfo,
} from "./planChange/planCatalog";
import { usePlanChangeFormat } from "./planChange/usePlanChangeFormat";

export enum EPlans {
  UMD_FREE = "UMD_FREE",
  UMD_FREE_MONTH = "UMD_FREE_MONTH",
  UMD_FREE_ANNUAL = "UMD_FREE_ANNUAL",
  UMD_PREMIUM_BUSINESS = "UMD_PREMIUM_BUSINESS",
  UMD_PREMIUM_BUSINESS_MONTH = "UMD_PREMIUM_BUSINESS_MONTH",
  UMD_PREMIUM_BUSINESS_ANNUAL_WITH_TRIAL = "UMD_PREMIUM_BUSINESS_ANNUAL_WITH_TRIAL",
  UMD_PREMIUM_BUSINESS_ANNUAL = "UMD_PREMIUM_BUSINESS_ANNUAL",
  SUMOM_8PS8HPOLTF = "SUMOM_8PS8HPOLTF",
  SUMOM_MHMYDHE9NH = "SUMOM_MHMYDHE9NH",
  SUMOM_USXDYFUDSY = "SUMOM_USXDYFUDSY",
  SUMOA_HVDYRRYCHY = "SUMOA_HVDYRRYCHY",
  SUMOA_T9IZI415ZG = "SUMOA_T9IZI415ZG",
  SUMOA_9X8ZNA5QAO = "SUMOA_9X8ZNA5QAO",
  SUMOA_HOYZZNJVYT = "SUMOA_HOYZZNJVYT",
  SUMOA_EUEIVLUAUW = "SUMOA_EUEIVLUAUW",
  UMD_ENTREPRISE_BUSINESS = "UMD_ENTREPRISE_BUSINESS",
  UMD_ENTREPRISE_BUSINESS_MONTH = "UMD_ENTREPRISE_BUSINESS_MONTH",
  UMD_ENTREPRISE_BUSINESS_ANNUAL_WITH_TRIAL = "UMD_ENTREPRISE_BUSINESS_ANNUAL_WITH_TRIAL",
  UMD_ENTREPRISE_BUSINESS_ANNUAL = "UMD_ENTREPRISE_BUSINESS_ANNUAL",
  SUMOM_GYASCZ0RSW = "SUMOM_GYASCZ0RSW",
  SUMOM_JVXKQNIFEN = "SUMOM_JVXKQNIFEN",
  SUMOM_PAISDXBDUS = "SUMOM_PAISDXBDUS",
  SUMOA_OGIDHUNOFG = "SUMOA_OGIDHUNOFG",
  SUMOA_YNL4PD6BOM = "SUMOA_YNL4PD6BOM",
  SUMOA_GSW9Q445J1 = "SUMOA_GSW9Q445J1",
  SUMOA_BOAXCZVLIQ = "SUMOA_BOAXCZVLIQ",
  SUMOA_VJTRNHTBIB = "SUMOA_VJTRNHTBIB",
  UMD_LARGE_BUSINESS = "UMD_LARGE_BUSINESS",
  UMD_LARGE_BUSINESS_MONTH = "UMD_LARGE_BUSINESS_MONTH",
  UMD_LARGE_BUSINESS_ANNUAL_WITH_TRIAL = "UMD_LARGE_BUSINESS_ANNUAL_WITH_TRIAL",
  UMD_LARGE_BUSINESS_ANNUAL = "UMD_LARGE_BUSINESS_ANNUAL",
  SUMOM_A8NQTU2UIB = "SUMOM_A8NQTU2UIB",
  SUMOM_7YV3KX83JA = "SUMOM_7YV3KX83JA",
  SUMOA_7AWE1A948A = "SUMOA_7AWE1A948A",
  SUMOA_ZN8D246RA5 = "SUMOA_ZN8D246RA5",
  SUMOA_ZEDIVSBYFC = "SUMOA_ZEDIVSBYFC",
  SUMOA_HZVGJGKYLM = "SUMOA_HZVGJGKYLM",
  SUMBU_EECBUSTIER1 = "SUMBU_EECBUSTIER1",
  SUMBU_EECBUSTIER2 = "SUMBU_EECBUSTIER2",
  SUMBU_EECBUSTIER3 = "SUMBU_EECBUSTIER3",
  SUMIN_STAFFTIER3 = "SUMIN_STAFFTIER3",
  SUM_BUSINESS_PARTNER = "SUM_BUSINESS_PARTNER",
}

export type PlanNames =
  | "UMD_FREE_MONTH"
  | "UMD_FREE_ANNUAL"
  | "UMD_PREMIUM_BUSINESS_MONTH"
  | "SUMOM_8PS8HPOLTF"
  | "SUMOM_MHMYDHE9NH"
  | "SUMOM_USXDYFUDSY"
  | "SUMOA_HVDYRRYCHY"
  | "UMD_PREMIUM_BUSINESS_ANNUAL_WITH_TRIAL"
  | "UMD_PREMIUM_BUSINESS_ANNUAL"
  | "SUMOA_T9IZI415ZG"
  | "SUMOA_EUEIVLUAUW"
  | "SUMOA_9X8ZNA5QAO"
  | "SUMOA_HOYZZNJVYT"
  | "SUMBU_EECBUSTIER1"
  | "UMD_ENTREPRISE_BUSINESS_MONTH"
  | "SUMOM_GYASCZ0RSW"
  | "SUMOM_JVXKQNIFEN"
  | "SUMOM_PAISDXBDUS"
  | "SUMOA_OGIDHUNOFG"
  | "UMD_ENTREPRISE_BUSINESS_ANNUAL_WITH_TRIAL"
  | "UMD_ENTREPRISE_BUSINESS_ANNUAL"
  | "SUMOA_YNL4PD6BOM"
  | "SUMOA_VJTRNHTBIB"
  | "SUMOA_GSW9Q445J1"
  | "SUMOA_BOAXCZVLIQ"
  | "SUMBU_EECBUSTIER2"
  | "UMD_LARGE_BUSINESS_MONTH"
  | "SUMOM_A8NQTU2UIB"
  | "SUMOM_7YV3KX83JA"
  | "UMD_LARGE_BUSINESS_ANNUAL_WITH_TRIAL"
  | "UMD_LARGE_BUSINESS_ANNUAL"
  | "SUMOA_7AWE1A948A"
  | "SUMOA_HZVGJGKYLM"
  | "SUMOA_ZN8D246RA5"
  | "SUMOA_ZEDIVSBYFC"
  | "SUMBU_EECBUSTIER3"
  | "SUMIN_STAFFTIER3"
  | "SUM_BUSINESS_PARTNER";

export type { CurrencyType };

export type PartnerPaidBy = {
  display_name: string | null;
  contact_email: string | null;
  auto_renew: boolean | null;
  ends_at: string | null;
};

export const decideGeneralName = (planName: PlanNames) => {
  switch (planName) {
    case "UMD_FREE_MONTH":
    case "UMD_FREE_ANNUAL":
    case "SUM_BUSINESS_PARTNER":
      return "BASIC";
    case "UMD_PREMIUM_BUSINESS_MONTH":
    case "SUMOM_8PS8HPOLTF":
    case "SUMOM_MHMYDHE9NH":
    case "SUMOM_USXDYFUDSY":
    case "SUMOA_HVDYRRYCHY":
    case "UMD_PREMIUM_BUSINESS_ANNUAL_WITH_TRIAL":
    case "UMD_PREMIUM_BUSINESS_ANNUAL":
    case "SUMOA_T9IZI415ZG":
    case "SUMOA_EUEIVLUAUW":
    case "SUMOA_9X8ZNA5QAO":
    case "SUMOA_HOYZZNJVYT":
    case "SUMBU_EECBUSTIER1":
      return "PREMIUM";
    case "UMD_ENTREPRISE_BUSINESS_MONTH":
    case "SUMOM_GYASCZ0RSW":
    case "SUMOM_JVXKQNIFEN":
    case "SUMOM_PAISDXBDUS":
    case "SUMOA_OGIDHUNOFG":
    case "UMD_ENTREPRISE_BUSINESS_ANNUAL_WITH_TRIAL":
    case "UMD_ENTREPRISE_BUSINESS_ANNUAL":
    case "SUMOA_YNL4PD6BOM":
    case "SUMOA_VJTRNHTBIB":
    case "SUMOA_GSW9Q445J1":
    case "SUMOA_BOAXCZVLIQ":
    case "SUMBU_EECBUSTIER2":
      return "ENTERPRISE";
    case "UMD_LARGE_BUSINESS_MONTH":
    case "SUMOM_A8NQTU2UIB":
    case "SUMOM_7YV3KX83JA":
    case "UMD_LARGE_BUSINESS_ANNUAL_WITH_TRIAL":
    case "UMD_LARGE_BUSINESS_ANNUAL":
    case "SUMOA_7AWE1A948A":
    case "SUMOA_HZVGJGKYLM":
    case "SUMOA_ZN8D246RA5":
    case "SUMOA_ZEDIVSBYFC":
    case "SUMBU_EECBUSTIER3":
    case "SUMIN_STAFFTIER3":
      return "LARGE_BUSINESSES";
    default:
      return "UNKNOWN";
  }
};

const Plans = ({
  user_identification,
  entity_identification,
  isInBilling = false,
  user_email,
  onCloseModal,
  targetPlan,
  access_token,
}: {
  user_identification: string;
  entity_identification: string;
  isInBilling?: boolean;
  user_email?: string;
  onCloseModal?: VoidFunction;
  targetPlan?: PlanTier;
  access_token?: string;
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const auth = useAuth();
  const permissions = usePermissions(auth.user);
  const isTeamMember = permissions.isRoleGoverned();

  const [searchParams] = useSearchParams();

  const { data: trialStatus } = useGetTrialStatus(
    user_identification,
    entity_identification
  );

  const { data: ownerPlans } = useGetOwnerPlans(
    user_identification,
    entity_identification,
    Boolean(access_token) || isTeamMember
  );
  const providedBy: PartnerPaidBy | undefined = ownerPlans?.planList?.find(
    (item: any) => item.paid_by
  )?.paid_by;

  const selfPaid = useMemo(
    () => findSelfPaidPlan(ownerPlans?.planList),
    [ownerPlans]
  );
  const isChangeMode =
    Boolean(selfPaid) && !providedBy && !isTeamMember && !access_token;
  const { data: planChanges } = usePlanChanges(
    user_identification,
    entity_identification,
    isChangeMode
  );
  const pendingPayment = planChanges?.find(
    (change) => change.status === "PENDING_PAYMENT"
  );
  const scheduledChange =
    planChanges?.find((change) => change.status === "SCHEDULED") ?? null;
  const [changeTarget, setChangeTarget] = useState<string | null>(null);
  const { k: pc, date: pcDate, tierName: pcTierName } = usePlanChangeFormat();

  const { data: ownerProfile } = useGetEntityUser({
    customer_identification: user_identification,
    entityIdentification: entity_identification,
    user_identification: user_identification,
  });

  const [isYearly, setIsYearly] = useState<boolean>(true);
  const [selectedCurrency, setSelectedCurrency] = useState<CurrencyType>("USD");

  const [loading, setLoading] = useState<boolean>(false);

  const freeFeatures = useMemo(
    () => getDifferentialPlanFeatures(PlanTier.FREE),
    []
  );
  const premiumFeatures = useMemo(
    () => getDifferentialPlanFeatures(PlanTier.PREMIUM),
    []
  );
  const enterpriseFeatures = useMemo(
    () => getDifferentialPlanFeatures(PlanTier.ENTERPRISE),
    []
  );
  const largeBusinessFeatures = useMemo(
    () => getDifferentialPlanFeatures(PlanTier.LARGE_BUSINESS),
    []
  );

  const planInheritance = {
    Free: null,
    Premium: "Free",
    Enterprise: "Premium",
    "Large businesses": "Enterprise",
  };

  const planTierToGeneralName = (tier: PlanTier): string => {
    const mapping: Record<PlanTier, string> = {
      [PlanTier.FREE]: "Free",
      [PlanTier.PREMIUM]: "Premium",
      [PlanTier.ENTERPRISE]: "Enterprise",
      [PlanTier.LARGE_BUSINESS]: "Large businesses",
      [PlanTier.BUSINESS_PARTNER]: "Free",
      [PlanTier.PROMO_ENTERPRISE_TRIAL]: "Promo Enterprise Trial",
    };
    return mapping[tier] || "";
  };

  const isTargetPlan = (planGeneralName: string): boolean => {
    if (!targetPlan) return false;
    return planTierToGeneralName(targetPlan) === planGeneralName;
  };

  const getPlanTierOrder = (planGeneralName: string): number => {
    const orderMapping: Record<string, number> = {
      Free: 0,
      Premium: 1,
      Enterprise: 2,
      "Large businesses": 3,
    };
    return orderMapping[planGeneralName] ?? 0;
  };

  const shouldShowPlan = (planGeneralName: string): boolean => {
    if (!targetPlan) return true;
    const targetOrder = getPlanTierOrder(planTierToGeneralName(targetPlan));
    const planOrder = getPlanTierOrder(planGeneralName);
    return planOrder >= targetOrder;
  };

  const isActiveTrial = (plan: any): boolean => {
    if (!trialStatus?.data?.has_active_trial || !trialStatus.data.trial_info)
      return false;

    const trialPlan = trialStatus.data.trial_info.plan;
    const planLabels = [
      ...Object.values(plan.label.monthly),
      ...Object.values(plan.label.annually),
    ];

    return planLabels.includes(trialPlan);
  };

  const getUserLimitText = (planTierName: string): string => {
    const tierMap: { [key: string]: PlanTier } = {
      Free: PlanTier.FREE,
      Premium: PlanTier.PREMIUM,
      Enterprise: PlanTier.ENTERPRISE,
      "Large businesses": PlanTier.LARGE_BUSINESS,
    };

    const tier = tierMap[planTierName];
    if (!tier) return "";

    const limit = USER_LIMITS[tier];

    if (tier === PlanTier.LARGE_BUSINESS || limit === Number.MAX_SAFE_INTEGER) {
      return t("Pricing.Section1.user_limit.unlimited") as string;
    }

    return t("Pricing.Section1.user_limit.up_to", { count: limit }) as string;
  };

  const getLeadLimitText = (planTierName: string): string => {
    const tierMap: { [key: string]: PlanTier } = {
      Premium: PlanTier.PREMIUM,
      Enterprise: PlanTier.ENTERPRISE,
      "Large businesses": PlanTier.LARGE_BUSINESS,
    };
    const tier = tierMap[planTierName];
    if (!tier) return "";
    return t("Pricing.Section1.lead_limit.up_to", {
      count: LEAD_LIMITS[tier],
    }) as string;
  };

  const getBoardLimitText = (planTierName: string): string => {
    const tierMap: { [key: string]: PlanTier } = {
      Free: PlanTier.FREE,
      Premium: PlanTier.PREMIUM,
      Enterprise: PlanTier.ENTERPRISE,
      "Large businesses": PlanTier.LARGE_BUSINESS,
    };
    const tier = tierMap[planTierName];
    if (!tier) return "";
    return t("Pricing.Section1.board_limit.up_to", {
      count: TODO_BOARD_LIMITS[tier],
    }) as string;
  };

  const LIMIT_INJECTIONS: Record<
    string,
    { anchor: string; showProspects: boolean; showBoards: boolean }
  > = {
    Premium: {
      anchor: "USER_DATA_SPACES_EXTEND_5",
      showProspects: true,
      showBoards: true,
    },
    Enterprise: {
      anchor: "SIGNUP_FORM_ENTERPRISE_LIMIT",
      showProspects: false,
      showBoards: true,
    },
    "Large businesses": {
      anchor: "SIGNUP_FORM_UNLIMITED",
      showProspects: false,
      showBoards: true,
    },
  };

  const getLimitRowsAfter = (planName: string, featureId: string) => {
    const cfg = LIMIT_INJECTIONS[planName];
    if (!cfg || cfg.anchor !== featureId) return [];
    const rows: Array<{
      key: string;
      Icon: typeof HiOutlineFilter;
      text: string;
    }> = [];
    if (cfg.showProspects && getLeadLimitText(planName)) {
      rows.push({
        key: "prospects",
        Icon: HiOutlineFilter,
        text: getLeadLimitText(planName),
      });
    }
    if (cfg.showBoards && getBoardLimitText(planName)) {
      rows.push({
        key: "boards",
        Icon: HiOutlineViewBoards,
        text: getBoardLimitText(planName),
      });
    }
    return rows;
  };

  const getRateLimitText = (planTierName: string): string => {
    const tierMap: { [key: string]: PlanTier } = {
      Free: PlanTier.FREE,
      Premium: PlanTier.PREMIUM,
      Enterprise: PlanTier.ENTERPRISE,
      "Large businesses": PlanTier.LARGE_BUSINESS,
    };

    const tier = tierMap[planTierName];
    if (!tier) return "";

    const rateLimit = RATE_LIMITS[tier];

    if (rateLimit === Number.MAX_SAFE_INTEGER) {
      return t("Pricing.Section1.rate_limit.unlimited") as string;
    }

    return t("Pricing.Section1.rate_limit.per_hour", {
      count: rateLimit,
    }) as string;
  };

  const getIconForCategory = (category: FeatureCategory) => {
    const iconMap = {
      [FeatureCategory.MESSAGING]: HiOutlineChatAlt2,
      [FeatureCategory.LOYALTY]: HiOutlineGift,
      [FeatureCategory.BUSINESS_CARDS]: HiOutlineIdentification,
      [FeatureCategory.INTEGRATION]: HiOutlineCode,
      [FeatureCategory.SUPPORT]: HiSupport,
      [FeatureCategory.USER_MANAGEMENT]: HiOutlineUsers,
      [FeatureCategory.PAYMENT]: HiCurrencyDollar,
      [FeatureCategory.EVENTS]: HiTicket,
      [FeatureCategory.SIGNATURE]: HiPencilAlt,
      [FeatureCategory.SEARCH]: HiOutlineSearch,
      [FeatureCategory.SIGNUP_FORM]: HiOutlineClipboardList,
      [FeatureCategory.AI]: HiOutlineSparkles,
    };
    return iconMap[category] || HiOutlineCog;
  };

  const plans: Array<{
    label: {
      monthly: {
        USD: string;
        EUR: string;
        CHF: string;
      };
      annually: {
        USD: string;
        USD_UPGRADE: string;
        EUR: string;
        EUR_UPGRADE: string;
        CHF: string;
        CHF_UPGRADE: string;
      };
    };
    title: string;
    description: string;
    price: {
      USD: {
        monthly: string;
        annually: string;
        discount: string;
      };
      EUR: {
        monthly: string;
        annually: string;
        discount: string;
      };
      CHF: {
        monthly: string;
        annually: string;
        discount: string;
      };
    };
    actionBtnText: string;
    features: Array<{ displayName: string; feature: string }>;
    generalName: string;
  }> = [
    {
      label: {
        monthly: {
          USD: "UMD_FREE_MONTH",
          EUR: "UMD_FREE_MONTH",
          CHF: "UMD_FREE_MONTH",
        },
        annually: {
          USD: "UMD_FREE_ANNUAL",
          USD_UPGRADE: "UMD_FREE_ANNUAL",
          EUR: "UMD_FREE_ANNUAL",
          EUR_UPGRADE: "UMD_FREE_ANNUAL",
          CHF: "UMD_FREE_ANNUAL",
          CHF_UPGRADE: "UMD_FREE_ANNUAL",
        },
      },
      title: t("Pricing.Section1.Basic.key1"),
      description: t("Pricing.Section1.Basic.key2"),
      price: {
        USD: {
          monthly: t("Pricing.Section1.Basic.key3"),
          annually: t("Pricing.Section1.Basic.key3"),
          discount: t("Pricing.Section1.Basic.key16"),
        },
        EUR: {
          monthly: t("Pricing.Section1.Basic.key3"),
          annually: t("Pricing.Section1.Basic.key3"),
          discount: t("Pricing.Section1.Basic.key16"),
        },
        CHF: {
          monthly: t("Pricing.Section1.Basic.key3"),
          annually: t("Pricing.Section1.Basic.key3"),
          discount: t("Pricing.Section1.Basic.key16"),
        },
      },
      actionBtnText: t("Pricing.Section1.Basic.key4"),
      features: freeFeatures
        .filter((f) => f.showInPricingPage)
        .map((f) => ({
          displayName: f.displayName,
          feature: f.feature as string,
        })),
      generalName: "Free",
    },
    {
      label: {
        monthly: {
          USD: "UMD_PREMIUM_BUSINESS_MONTH",
          EUR: "SUMOM_8PS8HPOLTF",
          CHF: "SUMOM_MHMYDHE9NH",
        },
        annually: {
          USD: "UMD_PREMIUM_BUSINESS_ANNUAL_WITH_TRIAL",
          USD_UPGRADE: "UMD_PREMIUM_BUSINESS_ANNUAL",
          EUR: "SUMOA_T9IZI415ZG",
          EUR_UPGRADE: "SUMOA_EUEIVLUAUW",
          CHF: "SUMOA_HOYZZNJVYT",
          CHF_UPGRADE: "SUMOA_HOYZZNJVYT",
        },
      },
      title: t("Pricing.Section1.Premium.key1"),
      description: t("Pricing.Section1.Premium.key2"),
      price: {
        USD: {
          monthly: "49",
          annually: "29",
          discount: "49",
        },
        EUR: {
          monthly: "49",
          annually: "29",
          discount: "49",
        },
        CHF: {
          monthly: "49",
          annually: "29",
          discount: "49",
        },
      },
      actionBtnText: t("Pricing.Section1.Premium.key3"),
      features: premiumFeatures
        .filter((f) => f.showInPricingPage)
        .map((f) => ({
          displayName: f.displayName,
          feature: f.feature as string,
        })),
      generalName: "Premium",
    },
    {
      label: {
        monthly: {
          USD: "UMD_ENTREPRISE_BUSINESS_MONTH",
          EUR: "SUMOM_GYASCZ0RSW",
          CHF: "SUMOM_JVXKQNIFEN",
        },
        annually: {
          USD: "UMD_ENTREPRISE_BUSINESS_ANNUAL_WITH_TRIAL",
          USD_UPGRADE: "UMD_ENTREPRISE_BUSINESS_ANNUAL",
          EUR: "SUMOA_YNL4PD6BOM",
          EUR_UPGRADE: "SUMOA_VJTRNHTBIB",
          CHF: "SUMOA_BOAXCZVLIQ",
          CHF_UPGRADE: "SUMOA_BOAXCZVLIQ",
        },
      },
      title: t("Pricing.Section1.Enterprise.key1"),
      description: t("Pricing.Section1.Enterprise.key2"),
      price: {
        USD: {
          monthly: "162",
          annually: "129",
          discount: "162",
        },
        EUR: {
          monthly: "162",
          annually: "129",
          discount: "162",
        },
        CHF: {
          monthly: "162",
          annually: "129",
          discount: "162",
        },
      },
      actionBtnText: t("Pricing.Section1.Enterprise.key3"),
      features: enterpriseFeatures
        .filter((f) => f.showInPricingPage)
        .map((f) => ({
          displayName: f.displayName,
          feature: f.feature as string,
        })),
      generalName: "Enterprise",
    },
    {
      label: {
        monthly: {
          USD: "UMD_LARGE_BUSINESS_MONTH",
          EUR: "SUMOM_A8NQTU2UIB",
          CHF: "SUMOM_7YV3KX83JA",
        },
        annually: {
          USD: "UMD_LARGE_BUSINESS_ANNUAL_WITH_TRIAL",
          USD_UPGRADE: "UMD_LARGE_BUSINESS_ANNUAL",
          EUR: "SUMOA_7AWE1A948A",
          EUR_UPGRADE: "SUMOA_HZVGJGKYLM",
          CHF: "SUMOA_ZN8D246RA5",
          CHF_UPGRADE: "SUMOA_ZEDIVSBYFC",
        },
      },
      title: t("Pricing.Section1.Large businesses.key1"),
      description: t("Pricing.Section1.Large businesses.key2"),
      price: {
        USD: {
          monthly: "",
          annually: "",
          discount: "",
        },
        EUR: {
          monthly: "",
          annually: "",
          discount: "",
        },
        CHF: {
          monthly: "",
          annually: "",
          discount: "",
        },
      },
      actionBtnText: t("Pricing.Section1.Large businesses.key3"),
      features: largeBusinessFeatures
        .filter((f) => f.showInPricingPage)
        .map((f) => ({
          displayName: f.displayName,
          feature: f.feature as string,
        })),
      generalName: "Large businesses",
    },
  ];

  const { mutate: freeSubscription, isLoading: isFreeLoading } =
    useFreeSubscription();

  const { mutate: ownerLoginSocial } = useOwnderLoginSocial();

  const getSelectedPlanId = (plan: any) =>
    isYearly
      ? plan.label.annually[`${selectedCurrency}_UPGRADE`]
      : plan.label.monthly[selectedCurrency];

  const handleLogPlanAndRedirect = (destination: string, plan: string) => {
    setLoading(true);

    try {
      if (plan === "UMD_FREE_MONTH" || plan === "UMD_FREE_ANNUAL") {
        if (auth.userEndedSubscription && auth.user.isAuthenticated) {
          try {
            const user = SecureStorage.getUserPreferences();
            if (user?.user_email) {
              const targetEmail = user?.user_email;
              freeSubscription(
                {
                  user_identification: user_identification,
                  entity_identification: entity_identification,
                  data: { email: targetEmail },
                },
                {
                  onSuccess: () => {
                    setLoading(false);
                    toast.success(
                      t("Pricing.success.free_subscription_activated")
                    );
                    onCloseModal?.();
                    window.location.reload();
                  },
                  onError(err) {
                    console.error("Free subscription error:", err);
                    setLoading(false);
                    toast.error(
                      err?.response?.data?.message ||
                        t("Pricing.errors.subscription_failed")
                    );
                  },
                }
              );
            } else {
              setLoading(false);
              toast.error(t("Pricing.errors.email_not_found"));
            }
          } catch (error) {
            console.error("Profile processing error:", error);
            setLoading(false);
            toast.error(t("Pricing.errors.profile_processing_failed"));
          }
        } else if (user_email) {
          freeSubscription(
            {
              user_identification: user_identification,
              entity_identification: entity_identification,
              data: { email: user_email },
              access_token,
            },
            {
              onSuccess: () => {
                try {
                  const credentials = SecureStorage.getOAuthCredentials();

                  if (credentials) {
                    ownerLoginSocial(
                      {
                        data: {
                          providerPayload: credentials,
                          type: "login",
                        },
                      },
                      {
                        onSuccess(data) {
                          try {
                            SecureStorage.clearOAuthCredentials();

                            auth.login({
                              ...data.data.element,
                              rememberMe: true,
                            });
                            setLoading(false);
                            toast.success(
                              t("Pricing.success.login_successful")
                            );
                            onCloseModal?.();

                            window.location.href = `/control-panel?plan=BASIC&plan_name=${plan}&status=true`;
                          } catch (loginError) {
                            console.error(
                              "Login processing error:",
                              loginError
                            );
                            setLoading(false);
                            toast.error(t("Pricing.errors.login_failed"));
                          }
                        },
                        onError: (err) => {
                          console.error("Social login error:", err);
                          setLoading(false);
                          toast.error(
                            err?.response?.data?.message ||
                              t("Pricing.errors.social_login_failed")
                          );
                        },
                      }
                    );
                  } else {
                    setLoading(false);
                    toast.success(
                      t("Pricing.success.free_subscription_activated")
                    );
                    onCloseModal?.();
                  }
                } catch (error) {
                  console.error(
                    "Free subscription success processing error:",
                    error
                  );
                  setLoading(false);
                  toast.error(
                    t("Pricing.errors.subscription_processing_failed")
                  );
                }
              },
              onError(err) {
                console.error("Free subscription error:", err);
                setLoading(false);
                toast.error(
                  err?.response?.data?.message ||
                    t("Pricing.errors.subscription_failed")
                );
              },
            }
          );
        } else {
          setLoading(false);
          toast.error(t("Pricing.errors.email_required"));
        }
      } else {
        if (typeof window !== "undefined") {
          try {
            SecureStorage.setSelectedPlan(plan);
            setLoading(false);
            window.location.href = destination;
          } catch (storageError) {
            console.error("Secure storage error:", storageError);
            setLoading(false);
            toast.error(t("Pricing.errors.storage_failed"));
          }
        } else {
          setLoading(false);
          toast.error(t("Pricing.errors.window_undefined"));
        }
      }
    } catch (error) {
      console.error("Unexpected error in handleLogPlanAndRedirect:", error);
      setLoading(false);
      toast.error(t("Pricing.errors.unexpected_error"));
    }
  };

  const ownerCountryCode = ownerProfile?.user_country_code || "";
  const lockedCurrency = isChangeMode ? selfPaid?.info.currency : undefined;
  const currentInterval = isChangeMode ? selfPaid?.info.interval : undefined;

  useEffect(() => {
    setSelectedCurrency(
      lockedCurrency ?? getCurrencyFromCountryCode(ownerCountryCode)
    );
  }, [ownerCountryCode, lockedCurrency]);

  useEffect(() => {
    setIsYearly(searchParams.get("plan_type") !== "monthly");
  }, [searchParams]);

  useEffect(() => {
    if (currentInterval && !searchParams.has("plan_type")) {
      setIsYearly(currentInterval === "annual");
    }
  }, [currentInterval, searchParams]);

  const GENERAL_NAME_TIER: Record<string, PaidTier> = {
    Premium: PlanTier.PREMIUM,
    Enterprise: PlanTier.ENTERPRISE,
    "Large businesses": PlanTier.LARGE_BUSINESS,
  };

  const changeBlocked =
    selfPaid?.cancel_at_period_end === true || Boolean(pendingPayment);

  const cardChange = (
    plan: (typeof plans)[number]
  ): { targetCode: string; kind: PlanChangeKind } | null => {
    if (!isChangeMode || !selfPaid || !GENERAL_NAME_TIER[plan.generalName]) {
      return null;
    }
    const targetCode = getSelectedPlanId(plan);
    return { targetCode, kind: classifyPlanChange(selfPaid.plan, targetCode) };
  };

  const isCurrentCard = (plan: (typeof plans)[number]) =>
    cardChange(plan)?.kind === "CURRENT";

  const ownsCta = (
    plan: (typeof plans)[number],
    change: ReturnType<typeof cardChange>
  ): change is { targetCode: string; kind: PlanChangeKind } =>
    Boolean(change) &&
    !(change!.kind === "QUOTE" && plan.generalName === "Large businesses");

  const isActionable = (kind: PlanChangeKind) =>
    kind === "UPGRADE" || kind === "PERIOD_END";

  const isScheduledTarget = (targetCode: string) => {
    const scheduledInfo = planInfo(scheduledChange?.to_plan_code);
    const targetInfo = planInfo(targetCode);
    return (
      Boolean(scheduledInfo && targetInfo) &&
      scheduledInfo!.tier === targetInfo!.tier &&
      scheduledInfo!.interval === targetInfo!.interval
    );
  };

  const changeCtaProps = (
    plan: (typeof plans)[number]
  ): Partial<ComponentProps<typeof CustomButton>> => {
    const change = cardChange(plan);
    if (!ownsCta(plan, change)) return {};
    const isCurrent = change.kind === "CURRENT";
    const sameTier = planInfo(change.targetCode)?.tier === selfPaid?.info.tier;
    return {
      variant: isCurrent ? "secondary" : "primary",
      onClick: () => {
        if (isActionable(change.kind)) {
          setChangeTarget(change.targetCode);
        } else if (!isCurrent) {
          navigate("/control-panel/support?requestType=billing_question");
          onCloseModal?.();
        }
      },
      disabled:
        isCurrent ||
        (isActionable(change.kind) &&
          (changeBlocked || isScheduledTarget(change.targetCode))),
      isLoading: false,
      title: isScheduledTarget(change.targetCode)
        ? pc("scheduledFor", { date: pcDate(scheduledChange?.effective_at) })
        : changeButtonTitle(change, sameTier),
    };
  };

  const renderChangeHint = (plan: (typeof plans)[number]) => {
    const change = cardChange(plan);
    if (!ownsCta(plan, change) || change.kind === "CURRENT") return null;
    if (isActionable(change.kind)) {
      if (!changeBlocked) return null;
      const endDate = pcDate(selfPaid?.current_period_end);
      return (
        <p className="text-xs text-amber-600 text-center mt-2">
          {selfPaid?.cancel_at_period_end
            ? endDate
              ? pc("resumeFirst", { date: endDate })
              : pc("resumeFirstNoDate")
            : pc("paymentPendingFirst")}
        </p>
      );
    }
    return (
      <p className="text-xs text-gray-500 text-center mt-2">
        {pc("largeBusinessSupport")}
      </p>
    );
  };

  const changeButtonTitle = (
    change: { targetCode: string; kind: PlanChangeKind },
    sameTier: boolean
  ) => {
    switch (change.kind) {
      case "CURRENT":
        return pc("currentPlan");
      case "UPGRADE":
        return sameTier
          ? pc("switchToAnnual")
          : pc("switchTo", { plan: pcTierName(change.targetCode) });
      case "PERIOD_END":
        return sameTier
          ? pc("switchToMonthlyAtRenewal")
          : pc("switchAtRenewal", { plan: pcTierName(change.targetCode) });
      default:
        return pc("contactSupport");
    }
  };

  return (
    <div className="plans-page px-2 sm:px-4">
      {selectedCurrency === "CHF" && (
        <div className="mb-6 bg-gradient-to-r from-red-50 to-white border-2 border-red-200 rounded-lg p-4 sm:p-6 shadow-sm">
          <div className="flex items-start gap-3">
            <div className="flex-shrink-0">
              <div className="w-10 h-10 bg-red-600 rounded-full flex items-center justify-center">
                <svg
                  className="w-6 h-6 text-white"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M5 13l4 4L19 7"
                  />
                </svg>
              </div>
            </div>
            <div className="flex-1">
              <h3 className="text-lg sm:text-xl font-bold text-gray-800 mb-4">
                {t("Pricing.Section1.chf_premium_features.title")}
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2 sm:gap-3">
                <div className="flex items-start gap-2">
                  <HiOutlineCheckCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                  <span className="text-sm text-gray-700">
                    {t("Pricing.Section1.chf_premium_features.expert_setup")}
                  </span>
                </div>
                <div className="flex items-start gap-2">
                  <HiOutlineCheckCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                  <span className="text-sm text-gray-700">
                    {t(
                      "Pricing.Section1.chf_premium_features.priority_onboarding"
                    )}
                  </span>
                </div>
                <div className="flex items-start gap-2">
                  <HiOutlineCheckCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                  <span className="text-sm text-gray-700">
                    {t(
                      "Pricing.Section1.chf_premium_features.dedicated_support"
                    )}
                  </span>
                </div>
                <div className="flex items-start gap-2">
                  <HiOutlineCheckCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                  <span className="text-sm text-gray-700">
                    {t("Pricing.Section1.chf_premium_features.local_payments")}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="items-center grid grid-cols-12 gap-3 sm:gap-6 mb-4 sm:mb-6">
        <div className="lg:col-span-6 sm:col-span-6 col-span-12 sm:text-left text-center px-2 sm:px-0">
          <h2 className="text-gray-700 text-xl sm:text-2xl font-bold font-figtree leading-none mb-1">
            {t("Pricing.Section1.key1")}
          </h2>
          <p className="text-gray-500 text-sm sm:text-base font-normal font-figtree leading-normal">
            {t("Pricing.Section1.key2")}
          </p>
          <p className="text-blue-600 text-xs sm:text-sm font-normal font-figtree leading-tight mt-auto">
            {t("Pricing.Section1.key3")}
          </p>
        </div>

        <div className="lg:col-span-6 sm:col-span-6 col-span-12 flex justify-center px-2 sm:px-0">
          <div className="w-full max-w-[280px] sm:max-w-xs md:w-80 h-[42px] p-0.5 bg-gray-100 rounded-lg justify-center items-center gap-0.5 inline-flex">
            <span
              data-testid="plans-selected-currency"
              data-currency={selectedCurrency}
              data-resolved={ownerProfile ? "true" : "false"}
              hidden
            />
            <button
              data-testid="plans-toggle-monthly"
              data-active={!isYearly}
              className={`grow shrink basis-0 h-[38px] px-2 sm:px-4 md:px-6 py-2 ${
                !isYearly
                  ? "bg-white border border-gray-200 border-opacity-10 text-gray-700 shadow"
                  : "bg-gray-100 text-gray-500"
              } rounded-md justify-center items-center flex text-xs sm:text-sm font-normal font-figtree leading-tight`}
              onClick={() => setIsYearly(false)}
            >
              {t("Pricing.Section1.key4")}
            </button>
            <button
              data-testid="plans-toggle-annual"
              data-active={isYearly}
              className={`grow shrink basis-0 h-[38px] px-2 sm:px-4 md:px-6 py-2 ${
                isYearly
                  ? "bg-white border border-gray-200 border-opacity-10 text-gray-700 shadow"
                  : "bg-gray-100 text-gray-500"
              } rounded-md justify-center items-center flex text-xs sm:text-sm font-normal font-figtree leading-tight`}
              onClick={() => setIsYearly(true)}
            >
              {t("Pricing.Section1.key5")}
            </button>
          </div>
        </div>
      </div>

      <div className="planing grid grid-cols-12 gap-3 sm:gap-6">
        {[
          ...(auth?.user?.customer_top_plan
            ? plans.filter((p) => p.generalName !== "Free")
            : plans),
        ]
          .filter((plan) =>
            isChangeMode
              ? plan.generalName !== "Free"
              : shouldShowPlan(plan.generalName)
          )
          .map((plan) => (
            <div
              key={plan.title}
              data-testid={`plan-card-${plan.generalName
                .toLowerCase()
                .replace(/\s+/g, "-")}`}
              data-plan-general-name={plan.generalName}
              className={`${
                auth?.user?.customer_top_plan
                  ? "lg:col-span-4"
                  : "lg:col-span-3"
              } lg:col-span-3 sm:col-span-6 col-span-12`}
            >
              <div
                className={`bg-white rounded-lg border ${
                  isCurrentCard(plan) || isTargetPlan(plan.generalName)
                    ? "border-blue-600 ring-2 ring-blue-200"
                    : (searchParams.has("plan_name") &&
                        Object.values(plan.label.annually).some(
                          (value) => value === searchParams.get("plan_name")
                        )) ||
                      (searchParams.has("plan_name") &&
                        Object.values(plan.label.monthly).some(
                          (value) => value === searchParams.get("plan_name")
                        ))
                    ? "border-blue-500"
                    : "border-gray-200 border-opacity-10"
                } flex-col justify-start items-start inline-flex w-full relative`}
              >
                {isCurrentCard(plan) && (
                  <div className="absolute -top-3 left-1/2 transform -translate-x-1/2 z-10">
                    <div className="bg-blue-600 text-white px-3 sm:px-4 py-1 rounded-full text-xs font-semibold whitespace-nowrap">
                      {pc("currentPlan")}
                    </div>
                  </div>
                )}

                {!isCurrentCard(plan) && isActiveTrial(plan) && (
                  <div className="absolute -top-3 left-1/2 transform -translate-x-1/2 z-10">
                    <div className="bg-blue-600 text-white px-3 sm:px-4 py-1 rounded-full text-xs font-bold shadow-lg whitespace-nowrap flex items-center gap-1">
                      <HiOutlineGift size={14} />
                      {t("plans.activeTrial")} -{" "}
                      {trialStatus?.data?.trial_info?.days_left}{" "}
                      {t("plans.daysLeft")}
                    </div>
                  </div>
                )}

                {!isCurrentCard(plan) &&
                  !isActiveTrial(plan) &&
                  isTargetPlan(plan.generalName) && (
                    <div className="absolute -top-3 left-1/2 transform -translate-x-1/2 z-10">
                      <div className="bg-gradient-to-r from-blue-600 to-blue-600 text-white px-3 sm:px-4 py-1 rounded-full text-xs font-bold shadow-lg whitespace-nowrap">
                        {t("plans.requiredPlan")}
                      </div>
                    </div>
                  )}

                {selectedCurrency === "CHF" &&
                  plan.generalName !== "Free" &&
                  !isCurrentCard(plan) &&
                  !isActiveTrial(plan) &&
                  !isTargetPlan(plan.generalName) && (
                    <div className="absolute -top-3 left-1/2 transform -translate-x-1/2 z-10">
                      <div className="bg-gradient-to-r from-red-600 to-red-700 text-white px-3 sm:px-4 py-1 rounded-full text-xs font-bold shadow-lg whitespace-nowrap flex items-center gap-1">
                        <svg
                          className="w-3 h-3"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path
                            fillRule="evenodd"
                            d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                            clipRule="evenodd"
                          />
                        </svg>
                        Swiss Premium
                      </div>
                    </div>
                  )}

                <div
                  className={`self-stretch px-3 sm:px-4 md:px-6 pt-4 sm:pt-5 md:pt-6 pb-4 sm:pb-6 flex-col justify-between items-start gap-6 sm:gap-8 flex min-h-[280px]`}
                >
                  <div className="self-stretch h-[84px] flex-col justify-start items-start gap-4 flex">
                    <div className="self-stretch text-gray-700 text-lg font-semibold font-figtree leading-7">
                      {plan.title}
                      {selectedCurrency === "CHF" &&
                        plan.generalName !== "Free" && (
                          <span className="ml-2 text-xs text-red-600 font-medium">
                            🇨🇭
                          </span>
                        )}
                    </div>
                    <div className="self-stretch text-gray-500 text-sm font-normal font-figtree leading-tight min-h-20">
                      {plan.description}
                    </div>
                  </div>

                  <div
                    className={`self-stretch justify-start gap-1 ${
                      isYearly
                        ? "items-start flex flex-col"
                        : "items-end inline-flex"
                    }`}
                  >
                    {plan.generalName === "Large businesses" ? (
                      <>
                        {/* design-ok: prix, page tarifs */}
                        <div className="text-gray-700 text-4xl font-bold font-figtree leading-10 flex items-end">
                          {t(
                            "Pricing.Section1.Large businesses.custom_pricing"
                          )}
                        </div>
                        {isYearly && (
                          <div className="text-gray-500 text-base font-normal font-figtree leading-normal invisible">
                            &nbsp;
                          </div>
                        )}
                      </>
                    ) : (
                      <>
                        {/* design-ok: prix, page tarifs */}
                        <div className="text-gray-700 text-4xl font-bold font-figtree leading-10 flex items-end">
                          {plan.generalName !== "Free" &&
                            selectedCurrency !== "EUR" &&
                            currencySymbolMapper[selectedCurrency]}{" "}
                          {isYearly
                            ? plan.price[selectedCurrency].annually
                            : plan.price[selectedCurrency].monthly}
                          {plan.generalName !== "Free" &&
                            selectedCurrency === "EUR" &&
                            " " + currencySymbolMapper[selectedCurrency]}
                          {plan.generalName !== "Free" && (
                            <div className="text-gray-700 text-2xl font-bold font-figtree leading-10 ml-2">
                              {t("Pricing.Section1.key8")}
                            </div>
                          )}
                        </div>
                        {isYearly && (
                          <div
                            className={`text-gray-500 text-base font-normal font-figtree ${
                              plan.generalName !== "Free" ? "line-through" : ""
                            } leading-normal`}
                          >
                            {plan.generalName !== "Free" &&
                              selectedCurrency !== "EUR" &&
                              currencySymbolMapper[selectedCurrency]}{" "}
                            {plan.price[selectedCurrency].discount}{" "}
                            {plan.generalName !== "Free" &&
                              selectedCurrency === "EUR" &&
                              currencySymbolMapper[selectedCurrency]}{" "}
                            {plan.generalName !== "Free" &&
                              t("Pricing.Section1.key8")}
                          </div>
                        )}
                      </>
                    )}
                  </div>

                  <CustomButton
                    data-testid={`plan-cta-${plan.generalName
                      .toLowerCase()
                      .replace(/\s+/g, "-")}`}
                    variant="primary"
                    onClick={() => {
                      if (plan.generalName === "Large businesses") {
                        navigate(
                          "/control-panel/support?requestType=large_enterprise_request"
                        );
                        onCloseModal?.();
                      } else {
                        const selectedPlanId = getSelectedPlanId(plan);
                        handleLogPlanAndRedirect(
                          `${
                            process.env.REACT_APP_PAYMENT_SERVICE
                          }/payment/subscription?template_image_url=https://cdn.1cdn.app/application/UMD/2405110558455_logo.png&redirect_page=${
                            process.env.REACT_APP_HOST_URL
                          }/${
                            isInBilling ? "control-panel/billing" : "login"
                          }&user_identification=${user_identification}&payment_plan_identification=${selectedPlanId}&language_code=en&entity_identification=${entity_identification}`,
                          selectedPlanId
                        );
                      }
                    }}
                    disabled={
                      plan.generalName !== "Large businesses" &&
                      (isTeamMember ||
                        Boolean(providedBy) ||
                        isFreeLoading ||
                        loading)
                    }
                    isLoading={
                      plan.generalName !== "Large businesses" &&
                      (isFreeLoading || loading)
                    }
                    title={
                      plan.generalName === "Large businesses"
                        ? t(
                            "Pricing.Section1.Large businesses.get_custom_quote"
                          )
                        : plan.actionBtnText
                    }
                    className=" w-full "
                    {...changeCtaProps(plan)}
                  />
                  {renderChangeHint(plan)}
                  {isTeamMember && (
                    <p className="text-xs text-amber-600 text-center mt-2">
                      {t("Pricing.operator_contact_owner")}
                    </p>
                  )}
                  {providedBy && plan.generalName !== "Large businesses" && (
                    <p className="text-xs text-amber-600 text-center mt-2">
                      {providedBy.display_name
                        ? t("Pricing.provided_by_contact", {
                            name: providedBy.display_name,
                          })
                        : t("Pricing.provided_by_partner_contact")}
                      {providedBy.contact_email && (
                        <>
                          {" "}
                          <a
                            href={`mailto:${providedBy.contact_email}`}
                            className="break-all font-medium text-blue-700 hover:underline"
                          >
                            {providedBy.contact_email}
                          </a>
                        </>
                      )}
                    </p>
                  )}
                </div>
                <div className="self-stretch h-px bg-gray-200" />
                <div className="self-stretch px-3 sm:px-4 md:px-6 pt-4 sm:pt-6 pb-5 sm:pb-8 md:pb-8 flex-col justify-start items-start gap-4 sm:gap-6 flex">
                  <div className="self-stretch flex-col justify-start items-start gap-3 sm:gap-4 flex">
                    {planInheritance[
                      plan.generalName as keyof typeof planInheritance
                    ] && (
                      <div className="self-stretch text-gray-600 text-sm font-medium font-figtree italic leading-tight">
                        {t("Pricing.Section1.inheritance_message", {
                          previousPlan:
                            planInheritance[
                              plan.generalName as keyof typeof planInheritance
                            ],
                        })}
                      </div>
                    )}
                    <div className="self-stretch justify-start items-start gap-3 inline-flex">
                      <HiOutlineCheckCircle className="w-5 h-5 text-green-500" />
                      <div className="grow shrink basis-0 text-gray-500 text-sm font-normal font-figtree leading-tight">
                        {getUserLimitText(plan.generalName)}
                      </div>
                    </div>

                    <div className="self-stretch justify-start items-start gap-3 inline-flex">
                      <HiOutlineCheckCircle className="w-5 h-5 text-green-500" />
                      <div className="grow shrink basis-0 text-gray-500 text-sm font-normal font-figtree leading-tight">
                        {getRateLimitText(plan.generalName)}
                      </div>
                    </div>

                    <div className="self-stretch h-px bg-gray-200" />

                    {plan.features.map((feature) => {
                      if (!feature || feature.displayName === "") return null;

                      const category =
                        FEATURE_CATEGORIES[
                          feature.feature as keyof typeof FEATURE_CATEGORIES
                        ];
                      const IconComponent = category
                        ? getIconForCategory(category)
                        : HiOutlineCog;

                      const injected = getLimitRowsAfter(
                        plan.generalName,
                        feature.feature
                      );

                      return (
                        <Fragment key={feature.displayName}>
                          <div className="self-stretch justify-start items-start gap-3 inline-flex">
                            <IconComponent className="w-5 h-5 text-gray-600 flex-shrink-0" />
                            <div className="grow shrink basis-0 text-gray-500 text-sm font-normal font-figtree leading-tight">
                              {t(`features.${feature.feature}`)}
                            </div>
                          </div>
                          {injected.map((row) => (
                            <div
                              key={row.key}
                              className="self-stretch justify-start items-start gap-3 inline-flex"
                            >
                              <row.Icon className="w-5 h-5 text-gray-600 flex-shrink-0" />
                              <div className="grow shrink basis-0 text-gray-500 text-sm font-normal font-figtree leading-tight">
                                {row.text}
                              </div>
                            </div>
                          ))}
                        </Fragment>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
          ))}
      </div>

      <p className="mt-8 text-center text-sm font-figtree text-gray-500">
        {t("Pricing.Section1.tax_note")}
      </p>

      {changeTarget && selfPaid && (
        <PlanChangeModal
          key={changeTarget}
          customerId={user_identification}
          entityId={entity_identification}
          fromPlan={selfPaid.plan}
          toPlan={changeTarget}
          ownerCountryCode={ownerCountryCode}
          scheduledChange={scheduledChange}
          onClose={() => setChangeTarget(null)}
          onDone={() => {
            setChangeTarget(null);
            onCloseModal?.();
          }}
        />
      )}
    </div>
  );
};

export default Plans;
